import React, { useContext, useState, useEffect } from 'react';
import { Download, Filter, X, Search, Eye, Edit, Trash2, FileText, Calendar, User, Plus, ChevronDown, EyeIcon } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import processService from '../../../services/processService';
import userService from '../../../services/userService';
import ProcessListItems from './cartographie/ProcessListItems';
import Pagination from '../../../components/Pagination';


export default function ManageCartographs() {
    const navigate = useNavigate();
    const {logoutUser, hasRole,user, authTokens } = useContext(AuthContext);
    const [showFiltersSection, setShowFiltersSection] = useState(false);
    const [selectedCartograph, setSelectedCartograph]=useState(null);

    const [filters, setFilters] = useState({
        process_name: '',
        assigned_user: '',
        probability: '',
        status: '',
        ordering: 'newest'
    });


    const [pagination, setPagination] = useState({
        count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0
    });

    const [processes, setProcesses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [userSearchLoading, setUserSearchLoading] = useState(false);




  
  const canAddCartograph = () => {
    return hasRole("RiskManager");
  }

  const canEditCartograph = () => {
    return hasRole("RiskManager");
  }

  const canDeleteCartograph = () => {
    return hasRole("Admin");
  }

  // Load processes on component mount and when filters change
  useEffect(() => {
    loadMyProcesses();
  }, [pagination.page, pagination.page_size]);

  const loadMyProcesses = async () => {
    setIsLoading(true);
    setError(null);
    
    try {

      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      const result = await processService.getMyProcesses(authTokens.access, filters, pagination_data);
      
      if (result.success) {
        setProcesses(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        console.error('Failed to load processes:', result.error);
      }
    } catch (error) {
      setError('Une erreur est survenue lors du chargement des processus');
      console.error('Error loading processes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const probabiliteOptions = ['Mineur', 'Normal', 'Majeur', 'Critique'];
  const statusOptions = ['draft', 'published', 'archived'];

    const handlePageChange = (newPage) => {
      setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newPageSize) => {
      setPagination(prev => ({ 
        ...prev, 
        page_size: newPageSize, 
        page: 1 // Reset to first page
      }));
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
        ...prev,
        [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
        process_name: '',
        assigned_user: '',
        probability: '',
        status: '',
        ordering: 'newest'
        });

        setPagination(prev => ({ ...prev, page: 1 }));
        // Fetch will be triggered by useEffect when filters change
        setTimeout(() => loadMyProcesses(), 0);
    };

    const applyFilters = () => {
        console.log('Applying filters:', filters);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
        loadMyProcesses();
    };

    const exportPDF = () => {
        console.log('Exporting to PDF...');
        // PDF export functionality
    };

    const handleShowFiltersSection = ()=>{
        showFiltersSection? setShowFiltersSection(false): setShowFiltersSection(true);
        if (!showFiltersSection) {
            setShowUserDropdown(false); // Close dropdown when hiding filters
        }
    };

    const handleUserSearch = async (e) => {
        const query = e.target.value;
        handleFilterChange('assigned_user', query);
        
        if (query.length >= 2) {
            setUserSearchLoading(true);
            try {
                const result = await userService.filterUsersBySearch(query, authTokens.access);
                if (result.success) {
                    setUserSearchResults(result.data);
                } else {
                    setUserSearchResults([]);
                }
            } catch (error) {
                console.error('Error searching users:', error);
                setUserSearchResults([]);
            } finally {
                setUserSearchLoading(false);
            }
        } else {
            setUserSearchResults([]);
        }
    };

    const selectUser = (user) => {
        handleFilterChange('assigned_user', user.email);
        setShowUserDropdown(false);
        setUserSearchResults([]);
    };

    // Close user dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserDropdown && !event.target.closest('.relative')) {
                setShowUserDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserDropdown]);


  const handleEditProcess = (process) => {
    // Navigate to edit page with risk ID
    navigate(`/dashboard/cartographies/edit/${process.id}`, { state: { process } });
  };

  const handleViewProcess = (process) => {
    navigate(`/dashboard/cartographies/${process.id}`);
  };

  const handleDeleteProcess = async (processId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce processus ?')) {
      try {
        const result = await processService.deleteProcess(processId, authTokens.access);
        
        if (result.success) {
          alert('Processus supprimé avec succès!');
          // Reload the processes list
          loadMyProcesses();
        } else {
          alert(`Erreur lors de la suppression: ${result.error}`);
          console.error('Process deletion failed:', result);
        }
      } catch (error) {
        console.error('Error deleting process:', error);
        alert('Une erreur est survenue lors de la suppression du processus');
      }
    }
  };
 return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Mes Cartographies</h2>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleShowFiltersSection}
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:shadow-md transition-all duration-200"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={()=>navigate('/dashboard/cartographies/all-cartographies')}
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:shadow-md transition-all duration-200"
          >
            <EyeIcon className="h-4 w-4 mr-2" />
            Voir tout
          </button>
{/* 
          <button
            onClick={exportPDF}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:shadow-md transition-all duration-200"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter PDF
          </button> */}

          {canAddCartograph() && (
            <Link
              to="/dashboard/cartographies/add"
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:shadow-md transition-all duration-200"
              >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une cartographie
            </Link>
          )}
        </div>
      </div>


      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">

            <div className='flex items-center justify-between'>
                <div className="flex items-center mb-4">
                    <Filter className="h-5 w-5 text-gray-500 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Filtres</h3>
                </div>

                <X onClick={handleShowFiltersSection} className="h-4 w-4 mr-2" />
            </div>


            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Process Name Filter */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du Processus
                </label>
                <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    value={filters.process_name}
                    onChange={(e) => handleFilterChange('process_name', e.target.value)}
                    placeholder="Rechercher par nom..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                </div>
            </div>

            {/* Assigned User Filter */}
            <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Utilisateur Assigné
                </label>
                <div className="relative">
                <input
                type="text"
                value={filters.assigned_user}
                onChange={handleUserSearch}
                onFocus={() => setShowUserDropdown(true)}
                placeholder="Rechercher utilisateur..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-8"
                />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                
                {/* User Dropdown */}
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {userSearchLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Recherche...</div>
                    ) : userSearchResults.length > 0 ? (
                      userSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => selectUser(user)}
                          className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{user.email}</div>
                          <div className="text-xs text-gray-500">
                            {user.first_name} {user.last_name}
                          </div>
                        </div>
                      ))
                    ) : filters.assigned_user ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Aucun utilisateur trouvé</div>
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">Tapez pour rechercher...</div>
                    )}
                  </div>
                )}
                </div>
            </div>

            {/* Status Filter */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
                </label>
                <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                <option value="">Tous les statuts</option>
                {statusOptions.map(option => (
                    <option key={option} value={option}>
                    {option}
                    </option>
                ))}
                </select>
            </div>

            {/* Ordering Filter */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Tri
                </label>
                <select
                value={filters.ordering}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                </select>
            </div>
            </div>

            {/* Filter Actions */}
            <div className="flex space-x-3">
            <button
                onClick={applyFilters}
                className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors duration-200"
            >
                <Filter className="h-4 w-4 mr-2" />
                Appliquer les filtres
            </button>
            <button
                onClick={clearFilters}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200"
            >
                <X className="h-4 w-4 mr-2" />
                Effacer les filtres
            </button>
            </div>
      </div>

      )}


      {/* Loading State */}
      {isLoading && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
          <div className="text-center">
            <div className="text-gray-500">Chargement des processus...</div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-700">
            <p className="font-medium">Erreur de chargement</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Table Section */}
      {!isLoading && !error && (
        <div className="bg-white  rounded-lg shadow-sm overflow-hidden">
            {/* {processes.map((process) => (
              <div className="overflow-x-auto border border-gray-200 mt-2">
                <ProcessListItems id={process.id} />
              </div>
            ))} */}

            <div className="overflow-x-auto border border-gray-200 mt-2">
              <table className="min-w-full bg-white text-xs border-collapse">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Nom du Processus</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Date Processus</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Élément à Contrôler</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Point Focal</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Statut</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Créé par</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Créé le</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Mis à jour par</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Mis à jour le</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Nom Activité</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Description Activité</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Nom Risque</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Description Risque</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Probabilité</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Consequence</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Statut Risque</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Action</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Date Action</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Assigné à</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Rôle</th>
                    <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                    <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {processes.map((process) => (
                    <ProcessListItems key={process.id} id={process.id} showActions={true}/>
                  ))}
                </tbody>
              </table>
            </div>

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.count}
            pageSize={pagination.page_size}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            itemName="processus" 
          />

          {/* Empty State */}
          {processes.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <p className="text-lg font-medium">Aucune cartographie trouvée</p>
                <p className="mt-1">Essayez de modifier vos filtres ou ajoutez de nouvelles cartographies.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
